import moment from "moment"
import 'moment/dist/locale/nl'
import 'moment/dist/locale/tr'
import * as sHelpers from "./string-helpers"

/**
 * Tarihi belirtilen formata göre formatlar.
 * @param {Date|string} d - Formatlanacak tarih
 * @param {string} f - Tarih formatı (varsayılan: "DD/MM/YYYY HH:mm")
 * @returns {string} - Formatlanmış tarih
 * format(new Date("2023-10-26T10:00:00"))	"26/10/2023 10:00"
 * format("2023-10-26", "DD.MM.YYYY")	"26.10.2023"
 */
export const format = (d, f = "DD/MM/YYYY HH:mm") =>
  d && moment(d).isValid() && moment(d).format(f)

/**
 * Tarihin geçmişte olup olmadığını kontrol eder.
 */
export const dateIsPast = d =>
  moment(d).isValid() && moment().diff(moment(d)) > 0

/**
 * Belirli bir dildeki tüm ayların adlarını alır.
 * @param {string} lang - Dil kodu (varsayılan: "en")
 */
export const getMonths = (lang = "en") =>
  moment.localeData(lang).months().map(sHelpers.capitalize)

/**
 * Türkçe ayların adlarını alır.
 */
export const getMonthsTr = (lang = "tr") =>
  moment.localeData(lang).months().map(sHelpers.capitalize)

/**
 * Saat stringini dakikaya çevirir.
 * @param {string} timeStr - Saat stringi (örn: "13:30")
 * @returns {number} - Dakika sayısını döndürür
 */
export const toMinutes = timeStr => {
  const [hour, minute] = timeStr.split(":").map(Number)

  return hour * 60 + minute
}

/**
 * Tarihi kısa formatta (gün-ay) formatlar (i18n destekli).
 */
export const formatDateShortI18n = (date, locale = 'tr') => {
  if (!date) return null
  const dt = new Date(date)
  if (isNaN(dt)) return null

  const localeMap = {
    'tr': 'tr-TR',
    'en': 'en-GB',
    'nl': 'nl-NL',
    'ar': 'ar-SA',
  }

  const mappedLocale = localeMap[locale] || locale || 'tr-TR'

  return new Intl.DateTimeFormat(mappedLocale, {
    day: "2-digit",
    month: "short",
  }).format(dt)
}

/**
 * Tarihi formatlar (i18n destekli).
 */
export const formatDateI18n = (date, locale = 'tr') => {
  if (!date) return null
  const dt = new Date(date)
  if (isNaN(dt)) return null

  const localeMap = {
    'tr': 'tr-TR',
    'en': 'en-GB',
    'nl': 'nl-NL',
    'ar': 'ar-SA',
  }

  const mappedLocale = localeMap[locale] || locale || 'tr-TR'

  return new Intl.DateTimeFormat(mappedLocale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(dt)
}

/**
 * Tarihi ve saati formatlar (i18n destekli).
 */
export const formatDateTimeI18n = (date, locale = 'tr') => {
  if (!date) return null
  const dt = new Date(date)
  if (isNaN(dt)) return null

  const localeMap = {
    'tr': 'tr-TR',
    'en': 'en-GB',
    'nl': 'nl-NL',
    'ar': 'ar-SA',
  }

  const mappedLocale = localeMap[locale] || locale || 'tr-TR'

  return new Intl.DateTimeFormat(mappedLocale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(dt)
}

/**
 * Saati formatlar (i18n destekli).
 */
export const formatTimeI18n = (date, locale = 'tr') => {
  if (!date) return null
  const dt = new Date(date)
  if (isNaN(dt)) return null

  const localeMap = {
    'tr': 'tr-TR',
    'en': 'en-GB',
    'nl': 'nl-NL',
    'ar': 'ar-SA',
  }

  const mappedLocale = localeMap[locale] || locale || 'tr-TR'

  return new Intl.DateTimeFormat(mappedLocale, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(dt)
}

/**
 * Tarihi formatlar (Türkçe format DD.MM.YYYY).
 */
export const formatDateTrTarih = date => {
  if (date && moment(date).isValid()) {
    return moment(date).format("DD.MM.YYYY")
  }

  return null
}

/**
 * Tarihi ve saati formatlar (Türkçe format DD.MM.YYYY HH:mm).
 */
export const formatDateTrTarihSaat = date => {
  if (date && moment(date).isValid()) {
    return moment(date).format("DD.MM.YYYY HH:mm")
  }

  return null
}

/**
 * Tarihi ve gün adını formatlar (i18n destekli).
 */
export const formatDateTrTarihGun = date => {
  if (!date) return null

  const locale = 'tr' || "tr"
  const dt = new Date(date)
  if (isNaN(dt)) return null

  const day = new Intl.DateTimeFormat(locale, { weekday: "long" }).format(dt)

  const formattedDate = dt.toLocaleDateString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  })

  return `${formattedDate} ${day}`
}

/**
 * Tarihi formatlar (YYYY-MM-DD).
 */
export const formatDate = date => {
  let parsed = moment(date, "DD.MM.YYYY")
  if (parsed.isValid()) {
    return moment(parsed).format("YYYY-MM-DD")
  } else {
    return null
  }
}

/**
 * Tarihi formatlar (dd/mm/yyyy).
 */
export const myDate = value => {
  if (value) {
    return moment(String(value)).format("dd/mm/yyyy")
  }
}

export const formatDateAyAdYil = value => {
  if (value) {
    const locale = "tr"
    moment.locale(locale)

    return moment(String(value)).format("MMMM YYYY")
  }
}

/**
 * Yaşı hesaplar.
 * @param {string} dateString - Doğum tarihi (DD/MM/YYYY formatında)
 * @returns {number} - Yaş
 */
export const calculateAge = dateString => {
  const birthDate = moment(dateString, "DD/MM/YYYY")

  if (!birthDate.isValid()) {
    return null
  }

  return moment().diff(birthDate, "years")
}
